<template>
	<v-container class="content" fluid>
		<v-layout column>
			<v-flex>
				<v-layout row align-center>
					<v-flex shrink mr-2>
						<h1 class="display-1 px-3">Aktuelle Aufträge</h1>
					</v-flex>
					<v-flex shrink>
						<v-btn
							class="blexon-button"
							icon
							small
							:disabled="dataLoading"
							title="Aktualisieren"
							@click="refresh()"
						>
							<v-icon>refresh</v-icon>
						</v-btn>
					</v-flex>
				</v-layout>
			</v-flex>
			<v-flex>
				<v-layout row wrap align-center>
					<v-flex xs12 sm4 class="px-3">
						<v-text-field
							v-model="suchbegriff"
							placeholder="Suchen nach Auftrag ID"
							clearable
							:hint="suchboxHint"
						></v-text-field>
					</v-flex>
					<v-flex xs12 sm2 class="px-3">
						<v-switch
							v-if="canSeeAllOrders"
							color="accent"
							v-model="showAllOrders"
							label="Alle anzeigen"
							title="Bereits fakturierte oder stornierte Aufträge auch anzeigen."
						>
						</v-switch>
					</v-flex>
					<v-flex xs12 sm6>
						<v-chip
							v-for="liefertermin in liefertermine"
							v-bind:key="liefertermin"
							v-bind:outline="!isLieferterminSelected(liefertermin)"
							@click="toggleLiefertermin(liefertermin)"
						>
							<v-avatar class="accent">
								{{ getNumberOfLieferscheine(liefertermin) }}
							</v-avatar>
							{{ liefertermin | formatDate }}
						</v-chip>
					</v-flex>
				</v-layout>
			</v-flex>
			<v-flex>
				<v-snackbar
					v-model="snackbar"
					:timeout="3000"
					:top="true"
					color="accent"
					class="black--text"
				>
					{{ snackbarText }}
					<v-btn class="blexon-button" small icon @click="snackbar = false">
						<v-icon>close</v-icon>
					</v-btn>
				</v-snackbar>
			</v-flex>
			<v-flex>
				<v-data-table
					:headers="customerOrderHeaders"
					:items="filteredOrders"
					:loading="dataLoading"
					item-key="orderId"
					hide-actions
					class="elevation-1"
				>
					<v-progress-linear
						slot="progress"
						color="accent"
						indeterminate
					></v-progress-linear>
					<template slot="items" slot-scope="props">
						<tr
							@click="props.expanded = !props.expanded"
							:class="getOrderRowClass(props.item)"
							:title="getOrderRowTitle(props.item)"
						>
							<td class="text-xs-left">
								<v-layout row>
									<v-icon
										v-bind:title="getStateText(props.item)"
										v-bind:style="{ color: getStateColor(props.item) }"
									>
										{{ getStateIcon(props.item) }}
									</v-icon>
									<v-icon
										v-if="hasActiveCancellationRequest(props.item.orderId)"
										@click="
											() => {
												if (
													cancellationWaitingForFertiger(
														props.item.orderId
													) &&
													!currentFertiger.isExternalPowderCoater
												) {
													goToEditCancellationRequest(props.item.orderId);
												}
											}
										"
										title="Stornierungsantrag erfasst"
										:style="
											currentFertiger.isExternalPowderCoater
												? ''
												: 'cursor:pointer;'
										"
										:color="
											cancellationWaitingForFertiger(props.item.orderId)
												? 'error'
												: 'grey'
										"
									>
										cancel
									</v-icon>
									<v-switch
										v-if="canManuallyTransmit"
										color="primary"
										v-model="props.item.manuallyTransmitted"
										@click.stop="manuallyTransmit(props.item, $event)"
										class="switchWithoutMessage"
										title="Einschalten, falls der Auftrag bereits manuell übertragen wurde."
									>
									</v-switch>
								</v-layout>
							</td>
							<td
								class="text-xs-left"
								:class="{
									replacementOrder: props.item.replacementOrderFor != null,
								}"
							>
								{{ props.item.orderId }}
							</td>
							<td class="text-xs-left">{{ props.item.customerId }}</td>
							<td
								class="text-xs-left"
								:title="props.item.receivedAt | formatReceivedAt"
							>
								{{ props.item.dueDate | formatLiefertermin }}<br />
							</td>
							<td class="text-xs-right">
								<span v-if="props.item.weight > props.item.weightOfParts">
									{{ props.item.weight | formatDigit }} kg
								</span>
								<span v-else>
									{{ props.item.weightOfParts | formatDigit }} kg
								</span>
							</td>
							<td class="text-xs-left">
								<span>
									{{ getTransportProviderName(props.item.transportProviderId) }}
									<v-icon
										color="primary"
										v-if="canEditTransport(props.item)"
										title="Transporteur ändern"
										@click="openChangeTransportProviderDialog(props.item)"
										x-small="true"
									>
										create
									</v-icon>
								</span>
							</td>
							<td>
								<!-- Lieferscheine können erst bezogen werden, wenn alle 
                                    Produktionsaufträge fehlerfrei sind. -->
								<span
									v-show="
										!currentFertiger.isExternalPowderCoater &&
										isTransmitted(props.item)
									"
								>
									<v-icon
										v-bind:color="
											props.item.customerOrderState ===
											customerOrderStates.None
												? 'accent'
												: 'success'
										"
										title="Fertigungsdokument"
										@click.stop="saveCustomerOrderAsInProduction(props.item)"
									>
										attach_file
									</v-icon>
									<v-icon>arrow_right</v-icon>
									<v-icon
										v-if="hasIncompleteCertificates(props.item)"
										v-bind:color="
											props.item.customerOrderState ===
											customerOrderStates.InProduction
												? 'accent'
												: 'success'
										"
										title="Zertifikat hochladen"
										v-bind:disabled="
											props.item.customerOrderState ===
												customerOrderStates.None ||
											hasActiveCancellationRequest(props.item.orderId)
										"
										@click.stop="navigateToZertificate(props.item)"
									>
										verified_user
									</v-icon>
									<v-icon
										v-else
										v-bind:color="
											props.item.customerOrderState ===
											customerOrderStates.InProduction
												? 'accent'
												: 'success'
										"
										title="Produziert"
										v-bind:disabled="
											props.item.customerOrderState ===
												customerOrderStates.None ||
											hasActiveCancellationRequest(props.item.orderId)
										"
										@click.stop="saveCustomerOrderAsFinished(props.item)"
									>
										done_outline
									</v-icon>
									<v-icon>arrow_right</v-icon>
									<v-icon
										v-bind:color="
											props.item.customerOrderState ===
											customerOrderStates.Produced
												? 'accent'
												: 'success'
										"
										:title="
											props.item.deliveryBlocked
												? 'Warten auf Blexon Freigabe'
												: 'Verpackt / Lieferschein'
										"
										v-bind:disabled="
											props.item.customerOrderState ===
												customerOrderStates.None ||
											props.item.customerOrderState ===
												customerOrderStates.InProduction ||
											props.item.transportProviderId == null ||
											props.item.creatingDelivery == true ||
											(props.item.deliveryBlocked && !canManuallyTransmit) ||
											hasActiveCancellationRequest(props.item.orderId)
										"
										@click.stop="createDeliveryOrGetLieferschein(props.item)"
									>
										mail
									</v-icon>
									<v-icon
										v-if="props.item.deliveryBlocked"
										color="error"
										title="Warten auf Blexon Freigabe"
									>
										warning
									</v-icon>
								</span>
							</td>
							<td>
								<v-btn
									v-if="canManuallyTransmit"
									title="Setzt den Status so, dass der Adapter den Auftrag überträgt."
									flat
									class="blexon-button"
									@click.stop="resend(props.item)"
								>
									Resend
								</v-btn>
							</td>
						</tr>
					</template>
					<!-- Produktionsaufträge -->
					<template slot="expand" slot-scope="props">
						<v-data-table
							:headers="productionOrderHeaders"
							:items="props.item.productionOrders"
							item-key="articleId"
							hide-actions
							class="elevation-1"
						>
							<template slot="items" slot-scope="prod">
								<tr :style="prod.item.isAssembly ? { color: 'grey' } : {}">
									<td class="text-xs-left">
										<span v-if="prod.item.parentPosition > 0"
											>{{ prod.item.parentPosition }}.</span
										>
										<span>{{ prod.item.position }}</span>
									</td>
									<td class="text-xs-left">{{ prod.item.menge }}</td>
									<td class="text-xs-left">
										<cad-preview
											:articleId="prod.item.articleId"
											width="100px"
											height="100px"
											:readonly="true"
										></cad-preview>
									</td>
									<td class="text-xs-left">
										Blexon Id: {{ prod.item.articleId }}<br />
										<span v-if="!prod.item.isAssembly">
											{{ prod.item.material }}<br />
										</span>
										{{ prod.item.laenge | formatDigit }} x
										{{ prod.item.breite | formatDigit }} x
										{{ prod.item.hoehe | formatDigit }} mm (LxBxH)<br />
										{{ prod.item.totalWeight | formatDigit }} kg (gesamte
										Position)
										<span v-if="prod.item.hasEngravings">
											<br />
											Gravur
										</span>
										<span v-if="prod.item.features.length > 0">
											<br />
											{{ prod.item.features }}
										</span>
										<span v-if="!prod.item.isAssembly">
											<br />
											{{ prod.item.entgratart }}
										</span>
										<span
											v-if="
												prod.item.weldlinesAsText != null &&
												prod.item.weldlinesAsText != ''
											"
										>
											<br />
											{{ prod.item.weldlinesAsText }}
										</span>
										<span v-if="prod.item.pulverbeschichten == true">
											<br />
											<span
												:style="
													'display:inline-block; height: 10px; width: 10px; background-color:' +
													prod.item.pulverbeschichtenHexColor
												"
											></span>
											&nbsp;RAL {{ prod.item.pulverbeschichtenRalColor }} |
											&nbsp;{{ prod.item.pulverbeschichtenTreatmentFace }} |
											&nbsp;{{ prod.item.pulverbeschichtenStructure }} |
											&nbsp;{{ prod.item.pulverbeschichtenFinish }}
											<span v-if="prod.item.pulverbeschichtenHasBaseCoating"
												>&nbsp;|&nbsp;vorgrundiert</span
											>
										</span>
										<span v-if="prod.item.verzinkung > 0">
											<br />
											{{ getZincText(prod.item.verzinkung) }}
										</span>
										<span
											v-if="prod.item.manufacturingComment !== ''"
											style="color: red"
										>
											<br />
											{{ prod.item.manufacturingComment }}
										</span>
										<span
											v-if="prod.item.certificateTypes > 0"
											style="color: red"
										>
											<br />
											<span>
												<a
													href="#"
													@click.prevent="
														navigateToZertificate(props.item)
													"
													>Zertifikat 3.1</a
												>
											</span>
										</span>
									</td>
									<td class="text-xs-left">
										<v-select
											v-if="canManuallyTransmit && !prod.item.isAssembly"
											v-model="prod.item.transmissionState"
											:items="transmissionStates"
											item-text="name"
											item-value="value"
											@change="updateProductionOrder(prod.item)"
										>
										</v-select>

										<span v-else>
											{{
												getTransmissionStateAsText(
													prod.item.transmissionState
												)
											}}
											<v-btn
												v-if="canManuallyTransmit"
												class="blexon-button"
												icon
												small
												title="Noch einmal übertragen"
												@click="deleteArticleTransfer(prod.item.articleId)"
											>
												<v-icon>refresh</v-icon>
											</v-btn>
										</span>
									</td>
									<td class="text-xs-left">
										<span
											:style="{
												visibility:
													prod.item.transmissionCode === 0
														? 'hidden'
														: 'visible',
											}"
										>
											({{ prod.item.transmissionCode }})
											<v-icon
												@click="openErrorDialog(prod.item)"
												color="error"
											>
												error_outline
											</v-icon>
										</span>
									</td>
									<td class="text-xs-left">
										<download-cad-component
											v-if="!prod.item.isAssembly"
											:translate="true"
											:articleId="prod.item.articleId"
										></download-cad-component>
									</td>
								</tr>
							</template>
						</v-data-table>
					</template>
					<template slot="no-data"> Bitte Liefertermin auswählen </template>
				</v-data-table>
			</v-flex>
			<!-- Gebinde definieren Dialog -->
			<v-dialog v-if="selectedOrder != null" v-model="deliveryOpen" persistent width="90%">
				<delivery-component
					:order="selectedOrder"
					ref="deliveryComponent"
					v-on:closed="deliveryOpen = false"
					v-on:saved="
						($event) => {
							deliveryOpen = false;
							createDeliveryAndGetLieferschein(selectedOrder, $event);
						}
					"
				>
				</delivery-component>
			</v-dialog>
			<!-- Error dialog zum Anzeigen eines Fehlers beim Laden in Boost -->
			<v-dialog v-model="errorDialogOpen" width="500">
				<v-card>
					<v-card-title class="headline grey lighten-2" primary-title>
						{{ errorDialogHeader }}
					</v-card-title>
					<v-card-text> Meldung von CAM System: {{ errorDialogText }} </v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn flat @click="errorDialogOpen = false"> Schliessen </v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<!-- Error dialog beim Abruf des Lieferscheins -->
			<v-dialog persistent v-model="lieferscheinErrorDialogOpen" width="500">
				<v-card>
					<v-card-title class="headline grey lighten-2" primary-title>
						Fehler beim Erstellen der Lieferung
					</v-card-title>
					<v-card-text>
						<ul>
							<li
								v-for="(message, index) in lieferscheinErrorMeldungen"
								v-bind:key="index"
							>
								{{ message }}
							</li>
						</ul>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn
							@click="
								lieferscheinErrorDialogOpen = false;
								blockDeliveryAndInformBlexon();
							"
						>
							Blexon informieren
						</v-btn>
						<v-btn
							class="blexon-button"
							@click="
								lieferscheinErrorDialogOpen = false;
								defineGebindeOrCreateDelivery(selectedOrder);
							"
						>
							Grösse anpassen und wiederholen
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<!-- Transport provider definieren Dialog -->
			<v-dialog
				persistent
				v-if="selectedOrder != null"
				v-model="changeTransportProviderOpen"
				width="500"
			>
				<v-card>
					<v-card-title class="headline grey lighten-2" primary-title>
						Transporteur auswählen
					</v-card-title>
					<v-card-text>
						<v-form ref="form" v-model="transportProviderValid">
							Neuen Provider wählen:
							<v-select
								placeholder="Bitte wählen"
								style="font-size: 13px"
								class="ma-0 pt-1"
								item-text="name"
								item-value="id"
								v-model="selectedTransportProviderId"
								:items="getTransportProviders(selectedOrder.weightOfParts)"
							>
							</v-select>
							<v-layout v-if="canManuallyTransmit" column>
								<v-flex xs12>
									<v-layout row align-center fill-height>
										<v-flex xs6 class="pr-2">
											<v-checkbox
												v-model="updateTransportCostForCustomer"
												:disabled="isUpdateTransportCostLocked"
												label="Transportkosten für den Kunden anpassen"
											>
											</v-checkbox>
										</v-flex>
										<v-flex
											xs6
											class="pr-2"
											v-show="updateTransportCostForCustomer"
										>
											<v-text-field
												v-model="newTransportCostTotal"
												:rules="transportCostRules"
												hint="Effektive Transportkosten, die Kunde bezahlen muss"
												label="Transportkosten"
											></v-text-field>
										</v-flex>
									</v-layout>
								</v-flex>
								<v-flex v-if="updateTransportCostForCustomer" xs12>
									Die Transportkosten werden für den Kunden angepasst. Der Kunde
									zahlt die Transportkosten, die oben definiert werden.
								</v-flex>
								<v-flex v-else xs12>
									Die Transportkosten für den Kunden werden nicht angepasst,
									ausser der Transport ist derzeit "Rampenabholung" oder "Nach
									Aufwand".
								</v-flex>
								<v-divider class="my-2"></v-divider>

								<!-- GRANT FERTIGER BONUS -->
								<v-flex xs12 class="mt-2">
									<h3>Fertiger Vergütung</h3>
								</v-flex>
								<v-flex xs12>
									<span>
										Dieser Betrag wird dem Fertiger in der Abrechnung für den
										Verpackungsaufwand gutgeschrieben. Wenn der Wert auf 0
										belassen wird, wird die gewöhnliche Berechnung für die
										Vergütung angewendet. Verpackungsmaterialien (Paletten,
										Schachteln, etc.) müssen ebenfalls in diesem Betrag
										enthalten sein. Der Betrag wird nicht zu den Transportkosten
										des Kunden dazugerechnet.
									</span>
								</v-flex>
								<v-flex xs12>
									<v-text-field
										v-model.number="fertigerWrappingCompensation"
										type="number"
										:rules="transportCostRules"
										label="Fertiger Vergütung"
									></v-text-field>
								</v-flex>
							</v-layout>
						</v-form>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<span
							v-if="
								(selectedTransportProviderId == 0 && !canManuallyTransmit) ||
								(selectedTransportProviderId == 5 && !canManuallyTransmit)
							"
							style="color: red"
						>
							Kann nur von Blexon bestimmt werden
						</span>
						<v-btn
							flat
							@click="
								changeTransportProviderOpen = false;
								selectedOrder = null;
							"
						>
							Schliessen
						</v-btn>
						<v-btn
							flat
							class="blexon-button"
							:disabled="
								selectedTransportProviderId == null ||
								!transportProviderValid ||
								(selectedTransportProviderId == selectedOrder.transportProviderId &&
									!canManuallyTransmit) ||
								(selectedTransportProviderId == 0 && !canManuallyTransmit) ||
								(selectedTransportProviderId == 5 && !canManuallyTransmit)
							"
							@click="setTransportProvider(selectedOrder)"
						>
							Speichern
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-layout>
	</v-container>
</template>

<script>
import httpClient from "../store/httpClient";
import DeliveryComponent from "./deliveryComponent";
import CustomerOrderStates from "./../dtos/customerOrderStates";
import moment from "moment";
import CadPreview from "./cadPreviewComponent.vue";
import DownloadCadComponent from "./downloadCadComponent.vue";
import { getZincCoatingText } from "@/dtos/zincCoating.js";

export default {
	components: {
		"delivery-component": DeliveryComponent,
		"cad-preview": CadPreview,
		"download-cad-component": DownloadCadComponent,
	},

	data() {
		return {
			suchboxHint: "",
			snackbar: false,
			snackbarText: "",
			errorDialogOpen: false,
			errorDialogHeader: "",
			errorDialogText: "",
			deliveryOpen: false,
			dataLoading: false,
			transmissionStates: [
				{
					name: "Gesendet",
					value: 1,
				},
				{
					name: "Übertragung fehlgeschlagen",
					value: 2,
				},
				{
					name: "Übertragen",
					value: 3,
				},
				{
					name: "Geladen",
					value: 4,
				},
				{
					name: "Laden fehlgeschlagen",
					value: 5,
				},
			],
			suchbegriff: "",
			customerOrders: [],
			productionOrderHeaders: [
				{
					value: "position",
					text: "Pos.",
					sortable: false,
					width: "1%",
				},
				{
					value: "menge",
					text: "Menge",
					sortable: false,
					width: "1%",
				},
				{
					value: "articleId",
					text: "Bild",
					sortable: false,
					width: "1%",
				},
				{
					value: "articleId",
					text: "Details",
					sortable: false,
					width: "10%",
				},
				{
					value: "transmissionState",
					text: "Status",
					sortable: false,
					width: "1%",
				},
				{
					value: "transmissionCode",
					text: "Code",
					sortable: false,
					width: "1%",
				},
				{
					value: "scdoc",
					text: "ScDoc",
					sortable: false,
					width: "1%",
				},
			],
			customerOrderHeaders: [
				{
					text: "Status",
					align: "left",
					sortable: false,
					width: "1%",
				},
				{
					text: "Auftrag ID",
					align: "left",
					sortable: false,
					value: "orderId",
					width: "1%",
				},
				{
					text: "Kundennr.",
					align: "left",
					sortable: false,
					value: "customerId",
					width: "1%",
				},
				{
					text: "Liefertermin",
					align: "left",
					sortable: false,
					value: "dueDate",
					width: "200px",
				},
				{
					text: "Gewicht",
					align: "right",
					sortable: false,
					value: "weight",
					width: "100px",
				},
				{
					text: "Transport",
					align: "left",
					sortable: false,
					value: "delivery",
					width: "200px",
				},
				{
					text: "Arbeitsfortschritt",
					align: "left",
					sortable: false,
					width: "300px",
				},
				{
					align: "right",
					sortable: false,
					width: "1%",
				},
			],
			selectedOrder: null,
			customerOrderStates: CustomerOrderStates,
			selectedLiefertermin: [],
			showAllOrders: false,
			lieferscheinErrorDialogOpen: false,
			// Messages created during creation of lieferschein
			lieferscheinErrorMeldungen: {},

			changeTransportProviderOpen: false,
			selectedTransportProviderId: null,
			newTransportCostTotal: 0.0,
			fertigerWrappingCompensation: 0.0,
			updateTransportCostForCustomer: false,
			transportProviderValid: false,
			isUpdateTransportCostLocked: false,
			transportCostRules: [
				(v) => v !== "" || "Kosten müssen angegeben werden.",
				(v) => !isNaN(v) || "Bitte Zahl eingeben",
				(v) => v >= 0 || "Anzahl muss grösser oder gleich 0 sein",
			],
		};
	},

	async created() {
		await this.loadCustomerOrders();
		this.loadOrderCancellationRequests();
		// find the closest liefertermin to now
		var defaultLiefertermin = this.liefertermine[0];
		var now = moment();
		var lastDifference = 100000;
		for (var i = 0; i < this.liefertermine.length; i++) {
			var diff = moment(this.liefertermine[i]).diff(now, "days");
			if (diff >= 0 && diff < lastDifference) {
				lastDifference = diff;
				defaultLiefertermin = this.liefertermine[i];
			}
		}

		if (sessionStorage.selectedLiefertermine !== undefined) {
			var storedDeliveryDates = JSON.parse(sessionStorage.selectedLiefertermine);
			for (const termin of storedDeliveryDates) {
				if (this.liefertermine.some((t) => t === termin)) {
					this.selectedLiefertermin.push(termin);
				}
			}
		}
		if (this.selectedLiefertermin.length === 0) {
			this.selectedLiefertermin.push(defaultLiefertermin);
		}
	},

	watch: {
		selectedLiefertermin() {
			sessionStorage.setItem(
				"selectedLiefertermine",
				JSON.stringify(this.selectedLiefertermin)
			);
		},
	},

	computed: {
		canManuallyTransmit() {
			// this is true if the user is an admin and false otherwise
			return this.currentFertiger.canManuallyTransmit;
		},

		canSeeAllOrders() {
			return this.currentFertiger.canSeeAllOrders;
		},

		filteredOrders() {
			var filteredOrders;
			if (this.showAllOrders) {
				filteredOrders = this.customerOrders;
			} else {
				filteredOrders = this.customerOrders.filter(
					(c) =>
						c.customerOrderState !== CustomerOrderStates.Abgeschlossen &&
						c.customerOrderState !== CustomerOrderStates.Fakturiert &&
						c.customerOrderState !== CustomerOrderStates.Storniert
				);
			}

			filteredOrders = filteredOrders.filter((c) =>
				this.selectedLiefertermin.includes(c.dueDate)
			);
			if (this.suchbegriff === "" || this.suchbegriff === null) {
				this.suchboxHint = "";
				return filteredOrders;
			} else {
				var trefferOhneLiefertermin = this.customerOrders.filter((c) =>
					c.orderId.includes(this.suchbegriff)
				);
				this.suchboxHint = "Bei der Suche werden die Liefertermine nicht berücksichtigt.";
				return trefferOhneLiefertermin;
			}
		},

		liefertermine() {
			var filteredOrders;
			if (this.showAllOrders) {
				filteredOrders = this.customerOrders;
			} else {
				filteredOrders = this.customerOrders.filter(
					(c) =>
						c.customerOrderState !== CustomerOrderStates.Abgeschlossen &&
						c.customerOrderState !== CustomerOrderStates.Fakturiert &&
						c.customerOrderState !== CustomerOrderStates.Storniert
				);
			}
			let result = [...new Set(filteredOrders.map((item) => item.dueDate))];
			result.sort((a, b) => moment(a).isAfter(moment(b)));
			return result;
		},
		orderCancellationRequests() {
			return this.$store.state.orderCancellationRequests;
		},

		currentFertiger() {
			return this.$store.getters.currentFertiger(this.$route.params.fertigerId);
		},
	},

	methods: {
		getZincText(zincCoating) {
			return getZincCoatingText(zincCoating);
		},

		hasIncompleteCertificates(order) {
			return order.productionOrders.some(
				(p) => p.certificateTypes > 0 && p.certificateId === 0
			);
		},

		getTransportProviderName(transportProviderId) {
			if (transportProviderId === 0) {
				return "Rampenabholung";
			}
			if (transportProviderId === null) {
				return "Bitte wählen";
			}
			return this.$store.state.transportProviders.filter(
				(t) => t.id == transportProviderId
			)[0].name;
		},
		async openChangeTransportProviderDialog(order) {
			try {
				this.dataLoading = true;
				this.selectedOrder = order;
				this.selectedTransportProviderId = order.transportProviderId;
				this.isUpdateTransportCostLocked = false;
				const transportResponse = await httpClient().get(
					`api/Delivery/GetModifyTransportData?orderId=${order.orderId}`
				);
				console.log(transportResponse.data);
				if (transportResponse.data.transportCostSetManually) {
					// If the cost was ever set manually, the checkbox can't be unchecked anymore.
					// But the actual cost can still be modified.
					this.isUpdateTransportCostLocked = true;
				}
				this.updateTransportCostForCustomer =
					transportResponse.data.transportCostSetManually;
				this.newTransportCostTotal = transportResponse.data.customerTransportCostOverride;
				this.fertigerWrappingCompensation = transportResponse.data.fertigerWrappingOverride;
				this.changeTransportProviderOpen = true;
			} catch (err) {
				this.setSnackbarText(
					"Transportdaten konnten nicht geladen werden. Blexon kontaktieren."
				);
			} finally {
				this.dataLoading = false;
			}
		},
		async setTransportProvider(order) {
			try {
				this.dataLoading = true;
				await httpClient().post(
					`api/Delivery/SetTransportProvider?customerOrderId=${order.orderId}` +
						`&transportProviderId=${this.selectedTransportProviderId}` +
						`&mustUpdateTransportPosition=${this.updateTransportCostForCustomer}` +
						`&newTransportCostTotal=${this.newTransportCostTotal}` +
						`&fertigerWrappingCompensation=${this.fertigerWrappingCompensation}`
				);
				order.transportProviderId = this.selectedTransportProviderId;
				this.setSnackbarText("Transport des Auftrags wurde erfolgreich geändert.");
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler beim Ändern des Auftrags. Blexon kontaktieren." + err);
			} finally {
				this.changeTransportProviderOpen = false;
				this.dataLoading = false;
			}
		},
		getTransportProviders(weightOfParts) {
			const currentFertigerRegionId = this.currentFertiger.regionId;
			let providers = [];
			if (this.canManuallyTransmit) {
				// if admin, return full list
				providers = this.$store.state.transportProviders.filter((t) =>
					t.regionIds.some((id) => id === currentFertigerRegionId)
				);
				providers.push({
					name: "Rampenabholung",
					id: 0,
				});
			} else {
				providers = this.$store.state.transportProviders.filter(
					(t) =>
						t.regionIds.some((id) => id === currentFertigerRegionId) &&
						t.weightLimit > weightOfParts &&
						t.id !== 5 // don't show Kurier for non-admins
				);
			}
			return providers;
		},
		/**
		 * Transport can only be edited by admins unless
		 * the transport is "Nach Aufwand".
		 * @param {*} order The order
		 */
		canEditTransport(order) {
			let isExternalPowderCoater = this.currentFertiger.isExternalPowderCoater;
			let isAdmin = this.canManuallyTransmit;
			let isShipped =
				order.customerOrderState == CustomerOrderStates.InDispatch ||
				order.customerOrderState == CustomerOrderStates.InDelivery ||
				order.customerOrderState == CustomerOrderStates.Delivered;
			let isNachAufwand = order.transportProviderId == null;
			if (isShipped || isExternalPowderCoater) return false;
			if (isAdmin) return true;
			if (isNachAufwand) return true;
			return false;
		},

		/**
		 * Loads the orders from the ERP, updates the Fertigerportal DB
		 * and then updates the UI
		 */
		async refresh() {
			try {
				this.dataLoading = true;
				var fertigerId = this.$route.params.fertigerId;
				await httpClient().post(`api/Production/Refresh?fertigerId=` + fertigerId);
				this.setSnackbarText("Aufträge aus dem Blexon ERP geladen.");
			} catch (err) {
				console.log(err);
				this.setSnackbarText(
					"Aufträge konnten nicht mit dem ERP synchronisiert werden. " + err
				);
			} finally {
				this.dataLoading = false;
			}
			await this.loadOrderCancellationRequests();
			await this.loadCustomerOrders();
		},

		/**
		 * Resets the state of the customer order and all
		 * production orders. Also reset the transmission code.
		 * The order is ready to be transmitted after this call.
		 */
		async resend(customerOrder) {
			try {
				this.dataLoading = true;
				await httpClient().post(
					`api/Production/ResetToResend?orderId=` + customerOrder.orderId
				);
				customerOrder.customerOrderState = CustomerOrderStates.None;
				customerOrder.manuallyTransmitted = false;
				customerOrder.productionOrders.forEach((p) => {
					p.transmissionState = 0;
					p.transmissionCode = 0;
				});
				this.setSnackbarText("Auftrag wird erneut übertragen.");
			} catch (err) {
				console.log(err);
			} finally {
				this.dataLoading = false;
			}
		},

		/**
		 * Sets the state of the customer order and all production orders
		 * to a state that marks them as transmitted.
		 */
		async manuallyTransmit(customerOrder, event) {
			try {
				this.dataLoading = true;
				await httpClient().post(
					`api/Production/SetToTransmitted?orderId=` + customerOrder.orderId
				);
				customerOrder.customerOrderState = CustomerOrderStates.None;
				customerOrder.manuallyTransmitted = true;
				customerOrder.productionOrders.forEach((p) => (p.transmissionState = 4));
			} catch (err) {
				console.log(err);
			} finally {
				this.dataLoading = false;
			}
		},

		/**
		 * Selects the specified Liefertermin, if it isn't
		 * selected or otherwise.
		 */
		toggleLiefertermin(liefertermin) {
			if (this.selectedLiefertermin.includes(liefertermin)) {
				var index = this.selectedLiefertermin.indexOf(liefertermin);
				this.selectedLiefertermin.splice(index, 1);
			} else {
				this.selectedLiefertermin.push(liefertermin);
			}
		},

		/**
		 * Returns the number of Lieferscheine on a certain date
		 */
		getNumberOfLieferscheine(liefertermin) {
			return this.customerOrders.filter((c) => c.dueDate == liefertermin).length;
		},

		/**
		 * Determines whether the specified liefertermin
		 * is selected or not
		 */
		isLieferterminSelected(liefertermin) {
			if (this.selectedLiefertermin.includes(liefertermin)) {
				return true;
			}
			return false;
		},

		/**
		 * Called when the delivery could not be created and the speditionsmitarbeiter
		 * decides to inform blexon
		 */
		async blockDeliveryAndInformBlexon() {
			try {
				this.dataLoading = true;
				var response = await httpClient().post(
					`api/Delivery/BlockDeliveryAndInformBlexon?` +
						`customerOrderId=${this.selectedOrder.orderId}`,
					this.lieferscheinErrorMeldungen
				);
				this.selectedOrder.deliveryBlocked = true;
			} catch (err) {
				this.setSnackbarText("Fehler bei der Benachrichtigung von Blexon. " + err);
			} finally {
				this.dataLoading = false;
				this.selectedOrder = null;
			}
		},

		/**
		 * Determines if a delivery already exists for this order.
		 * If yes, gets the delivery note for it.
		 * If no, opens the dialog for defining the container or creates the delivery directly.
		 * @param {*} customerOrder Order to process.
		 */
		createDeliveryOrGetLieferschein(customerOrder) {
			// Do nothing if external powder coater or no provider selected
			if (
				this.currentFertiger.isExternalPowderCoater ||
				customerOrder.transportProviderId == null
			) {
				return;
			}
			if (customerOrder.customerOrderState === CustomerOrderStates.Produced) {
				this.defineGebindeOrCreateDelivery(customerOrder);
			} else {
				this.getLieferschein(customerOrder);
			}
		},

		/**
		 * Opens the container dialog if containers need to be defined for this delivery.
		 * Otherwise directly creates the delivery.
		 */
		defineGebindeOrCreateDelivery(customerOrder) {
			var providers = this.$store.state.transportProviders.filter(
				(t) => t.id == customerOrder.transportProviderId
			);
			// Check if Gebinde dialog is shown or if delivery is created / downloaded directly
			// Only in case of Rampenabholung, the delivery is created without definitng the Gebinde
			if (providers.length > 0) {
				this.selectedOrder = customerOrder;
				// we need a timeout, so the new order is actually
				// set in the component
				setTimeout(() => {
					this.$refs.deliveryComponent.loadGebinde();
					this.deliveryOpen = true;
				}, 10);
			} else {
				this.createDeliveryAndGetLieferschein(customerOrder, true);
			}
		},

		async createDeliveryAndGetLieferschein(customerOrder, compareTransportCost) {
			const success = await this.createDelivery(customerOrder, compareTransportCost);
			if (success) {
				await this.getLieferschein(customerOrder);
			}
		},

		/**
		 * Calls the backend to create a new Lieferung
		 * The backend will not create a Lieferung twice.
		 * It generates and downloads the Lieferschein to the backend.
		 * It downloads just meta information which is used to
		 * download the Lieferschein and the labels.
		 */
		async createDelivery(customerOrder, compareTransportCost) {
			try {
				this.dataLoading = true;
				customerOrder.creatingDelivery = true;
				var response = await httpClient().post(
					`api/Production/CreateDelivery?` +
						`orderId=${customerOrder.orderId}&compareTransportCost=${compareTransportCost}`,
					null
				);

				if (response.data.created == true) {
					if (customerOrder.customerOrderState === CustomerOrderStates.Produced) {
						customerOrder.customerOrderState = CustomerOrderStates.InDispatch;
					}
					customerOrder.deliveryBlocked = false;
					this.selectedOrder = null;
					return true;
				} else {
					this.lieferscheinErrorMeldungen = response.data.messages;
					this.lieferscheinErrorDialogOpen = true;
					return false;
				}
			} catch (err) {
				this.setSnackbarText("Fehler beim Erstellen der Lieferung. " + err);
				return false;
			} finally {
				this.dataLoading = false;
				customerOrder.creatingDelivery = false;
			}
		},

		/**
		 * Get the delivery note from the backend and downloads it as a PDF.
		 * Also downloads labels if there are any.
		 * @param {*} customerOrder Order to get delivery note for.
		 */
		async getLieferschein(customerOrder) {
			this.downloadLieferschein(customerOrder);
			const response = await httpClient().get(
				`api/Production/GetLieferscheinLabelCount?` + `orderId=${customerOrder.orderId}`
			);
			for (var i = 0; i < response.data; i++) {
				this.downloadLabel(customerOrder, i);
			}
		},

		/**
		 * Calls the backend to download the delivery note
		 */
		async downloadLieferschein(customerOrder) {
			try {
				var response = await httpClient().post(
					`api/Production/DownloadLieferschein?` + `orderId=${customerOrder.orderId}`,
					null,
					{
						responseType: "blob",
					}
				);
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", "Lieferschein " + customerOrder.orderId + ".pdf");
				link.click();
			} catch (err) {
				this.setSnackbarText("Fehler beim Download des Lieferscheins. " + err);
			}
		},

		/**
		 * Calls the backend to download a single label (Example DHL)
		 */
		async downloadLabel(customerOrder, number) {
			try {
				var response = await httpClient().post(
					`api/Production/DownloadLabel?` +
						`orderId=${customerOrder.orderId}&number=${number}&transportProviderId=${customerOrder.transportProviderId}`,
					null,
					{
						responseType: "blob",
					}
				);
				if (response.status != 200) {
					return;
				}
				let pdfName = `Label ${customerOrder.orderId}_${number + 1}.pdf`;
				if (customerOrder.transportProviderId === 1) {
					// name file "Lieferschein" if it's Post
					pdfName = `Lieferschein ${customerOrder.orderId}_${number + 1}.pdf`;
				}
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", pdfName);
				link.click();
			} catch (err) {
				this.setSnackbarText("Fehler beim Download des Labels. " + err);
			}
		},

		/**
		 * Marks this customer order as finished. This means
		 * all positions of the order have been produced.
		 * They are not ready to be sent.
		 * After this step it will be possible to get the Lieferschein.
		 */
		async saveCustomerOrderAsFinished(customerOrder) {
			// The button can be clicked when the state is InProduction AND later
			// When the state is later, the click is ignored.
			if (customerOrder.customerOrderState !== CustomerOrderStates.InProduction) {
				return;
			}
			try {
				this.dataLoading = true;
				await httpClient().post(
					`api/Production/CustomerOrderFinished?orderId=` + customerOrder.orderId
				);
				customerOrder.customerOrderState = CustomerOrderStates.Produced;
				this.setSnackbarText("Auftrag als produziert an Blexon gemeldet.");
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Auftrag konnte nicht an Blexon gemeldet werden. " + err);
			} finally {
				this.dataLoading = false;
			}
		},

		/**
		 * Marks this customer order as in production if not already in this state.
		 * They are not produced yet.
		 * After this step it will open the Fertigungsbegleitdokument.
		 */
		async saveCustomerOrderAsInProduction(customerOrder) {
			try {
				this.dataLoading = true;
				if (customerOrder.customerOrderState === CustomerOrderStates.None) {
					await httpClient().post(
						`api/Production/CustomerOrderStarted?orderId=` + customerOrder.orderId
					);
					this.setSnackbarText('Auftrag wird als "In Produktion" markiert.');
					customerOrder.customerOrderState = CustomerOrderStates.InProduction;
				}
				var response = await httpClient().post(
					"api/Production/GetFertigunsbegleitdokument?orderId=" + customerOrder.orderId,
					null,
					{
						responseType: "blob",
					}
				);
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", "Auftrag " + customerOrder.orderId + ".pdf");
				link.click();
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler bei Auftragsmeldung: " + err);
			} finally {
				this.dataLoading = false;
			}
		},

		/**
		 * Returns the color for the state of the specified customer order.
		 * The state is based on the production order state.
		 */
		getStateColor(customerOrder) {
			switch (customerOrder.customerOrderState) {
				case CustomerOrderStates.New:
					return "grey";
				case CustomerOrderStates.None:
				case CustomerOrderStates.InProduction:
					if (this.isTransmitted(customerOrder)) {
						if (customerOrder.manuallyTransmitted) {
							return "lightgreen";
						}
						return "green";
					} else {
						return "red";
					}
				case CustomerOrderStates.Produced:
				case CustomerOrderStates.InDispatch:
				case CustomerOrderStates.InDelivery:
				case CustomerOrderStates.Delivered:
					return "green";
				case CustomerOrderStates.Fakturiert:
				case CustomerOrderStates.Storniert:
				case CustomerOrderStates.Abgeschlossen:
					return "#efbb20";
				default:
					return "red";
			}

			if (customerOrder.customerOrderState === CustomerOrderStates.New) {
			} else if (customerOrder.customerOrderState === CustomerOrderStates.Deleted) {
				return "grey";
			} else {
			}
		},

		/**
		 * Returns the tooltip text explaining the state of the specified customer order.
		 * The state is based on the production order state.
		 */
		getStateText(customerOrder) {
			switch (customerOrder.customerOrderState) {
				case CustomerOrderStates.New:
					return "Dieser Auftrag ist neu und wird erst morgen übertragen.";
				case CustomerOrderStates.None:
				case CustomerOrderStates.InProduction:
					if (this.isTransmitted(customerOrder)) {
						if (customerOrder.manuallyTransmitted) {
							return "Manuell (per E-Mail) übertragen";
						}
						return "Erfolgreich übertragen";
					} else {
						return "Alle oder einige Teile wurden nicht übertragen.";
					}
				case CustomerOrderStates.Produced:
					return "Produziert";
				case CustomerOrderStates.InDispatch:
				case CustomerOrderStates.InDelivery:
				case CustomerOrderStates.Delivered:
					return "Versandt";
				case CustomerOrderStates.Fakturiert:
					return "Rechnung gestellt";
				case CustomerOrderStates.Storniert:
					return "Storniert";
				case CustomerOrderStates.Unvollstaendig:
					return "Status im Erp Unvollständig";
				case CustomerOrderStates.Abgeschlossen:
					return "Status im Erp Abgeschlossen";
				case CustomerOrderStates.TerminUeberschritten:
					return "Status im Erp Termin überschritten";
				case CustomerOrderStates.Gestoert:
					return "Status im Erp Gestört";
				case CustomerOrderStates.Paid:
					return "Status im Erp Bezahlt";
				default:
					return "Status unbekannt";
			}
		},

		getStateIcon(customerOrder) {
			switch (customerOrder.customerOrderState) {
				case CustomerOrderStates.New:
				case CustomerOrderStates.None:
				case CustomerOrderStates.InProduction:
					return "bookmarks";
				case CustomerOrderStates.Produced:
				case CustomerOrderStates.Abgeschlossen:
				case CustomerOrderStates.Paid:
					return "check_circle";
				case CustomerOrderStates.InDispatch:
				case CustomerOrderStates.InDelivery:
				case CustomerOrderStates.Delivered:
					return "local_shipping";
				case CustomerOrderStates.Fakturiert:
					return "attach_money";
				case CustomerOrderStates.Storniert:
					return "delete_forever";
				default:
					return "warning";
			}
		},

		getOrderRowClass(customerOrder) {
			const dueDate = moment(moment(customerOrder.dueDate).format("YYYY-MM-D"));
			const today = moment(moment().format("YYYY-MM-D"));
			if (customerOrder.customerOrderState != CustomerOrderStates.Delivered) {
				if (dueDate.isBefore(today) && customerOrder.delivery.includes("LKW")) {
					return "late";
				}
				if (dueDate.isSame(today) && customerOrder.delivery.includes("LKW")) {
					return "hurry";
				}
			}
		},

		getOrderRowTitle(customerOrder) {
			const dueDate = moment(moment(customerOrder.dueDate).format("YYYY-MM-D"));
			const today = moment(moment().format("YYYY-MM-D"));
			if (dueDate.isBefore(today)) {
				return "Auftrag ist verspätet.";
			}
			if (dueDate.isSame(today) && customerOrder.delivery.includes("LKW")) {
				return "LKW Auftrag bis 10:00 Uhr aufgeben.";
			}
		},

		/**
		 * Returns the state of the specified customer order.
		 * The state is based on the production order state.
		 */
		isTransmitted(customerOrder) {
			for (var i = 0; i < customerOrder.productionOrders.length; i++) {
				if (customerOrder.customerOrderState === CustomerOrderStates.Storniert) {
					return false;
				}
				var productionOrder = customerOrder.productionOrders[i];
				if (
					productionOrder.transmissionState !== 4 ||
					productionOrder.transmissionCode !== 0
				) {
					return false;
				}
			}
			return true;
		},

		getProcessColor(customerOrder) {
			return "success";
		},

		/**
		 * Loads all open customer orders with their production orders
		 * from the server.
		 */
		async loadCustomerOrders() {
			try {
				this.dataLoading = true;
				var fertigerId = this.$route.params.fertigerId;
				let response = await httpClient().get(
					`api/Production/GetCustomerOrders?fertigerId=` + fertigerId
				);
				this.customerOrders = response.data;
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler beim Laden der Aufträge. " + err);
			} finally {
				this.dataLoading = false;
			}
		},

		// Loads the order cancellation requests to show an icon
		async loadOrderCancellationRequests() {
			try {
				var fertigerId = this.$route.params.fertigerId;
				let response = await httpClient().get(
					`api/Production/GetOrderCancellationRequests?fertigerId=` + fertigerId
				);
				this.$store.commit("setOrderCancellationRequests", response.data);
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler beim Laden der Stornierungsanträge. " + err);
			}
		},

		/**
		 * Saves the new state of the production order on the server.
		 */
		async updateProductionOrder(productionOrder) {
			try {
				this.dataLoading = true;
				let response = await httpClient().post(
					`api/Production/updateProductionOrder`,
					productionOrder
				);
				console.log("update productionOrder successfully");
			} catch (err) {
				console.log(err);
			} finally {
				this.dataLoading = false;
			}
		},

		openErrorDialog(productionOrder) {
			this.errorDialogHeader = productionOrder.transmissionMessage;
			this.errorDialogText = productionOrder.errorMessage;
			this.errorDialogOpen = true;
		},

		/**
		 * Sets the text in the snackbar and shows it.
		 */
		setSnackbarText(text) {
			this.snackbarText = text;
			this.snackbar = true;
		},

		hasActiveCancellationRequest(blexonOrderId) {
			return (
				this.orderCancellationRequests !== null &&
				this.orderCancellationRequests.some((o) => o.blexonOrderId == blexonOrderId)
			);
		},

		cancellationWaitingForFertiger(blexonOrderId) {
			let request = this.orderCancellationRequests.filter(
				(o) => o.blexonOrderId == blexonOrderId
			)[0];
			return request.state == 0;
		},

		goToEditCancellationRequest(orderId) {
			this.$router.push({
				name: "orderCancellation",
				params: {
					fertigerId: this.$route.params.fertigerId,
					orderId: orderId,
				},
			});
		},
		navigateToZertificate(order) {
			var fertigerId = this.$route.params.fertigerId;
			this.$router.push({
				name: "orderCertificates",
				params: { fertigerId: fertigerId, orderId: order.orderId },
			});
		},

		getTransmissionStateAsText(state) {
			return this.transmissionStates.filter((s) => s.value === state).length > 0
				? this.transmissionStates.filter((s) => s.value === state)[0].name
				: "Neu";
		},

		async deleteArticleTransfer(articleId) {
			await httpClient().post(
				`api/transfer/deleteArticleTransfer?articleId=${articleId}`,
				null
			);
			this.setSnackbarText(
				"Baugruppe wird beim nächsten Poll des Adapters wieder übertragen. Ca. 20 Minuten."
			);
		},
	},
};
</script>

<style>
.v-dialog.specialSize {
	max-height: 100% !important;
	margin: 12px;
}
.replacementOrder {
	color: red;
}
.switchWithoutMessage {
	padding-left: 1em;
	max-width: 20px;
}
.switchWithoutMessage > div > div.v-input__slot {
	margin-bottom: 0;
}
.switchWithoutMessage > div > div.v-messages {
	min-height: 2px;
}

.late {
	border-left: 20px solid red;
}
.hurry {
	border-left: 20px solid orange;
}
</style>
